/**
 * Created by chachora on 12/19/18.
 */
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import moment from 'moment';
import * as utils from '../../helpers/utils';
import * as uralReport from '../../reports/ural-report';
import * as dpReport from '../../reports/dp-report';
import * as eventHelper from '../../helpers/event-helper';
import * as mobileHelper from '../../helpers/mobile';

import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import withStyles from '@mui/styles/withStyles';
import PhoneIcon from '@mui/icons-material/Phone';
import InboundIcon from '@mui/icons-material/FlightLand';
import OutboundIcon from '@mui/icons-material/FlightTakeoff';
import GetAppIcon from '@mui/icons-material/GetApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state';
import InboundForm from './EventInboundForm';
import OutboundForm from './EventOutboundForm';
import EventContacts from './EventContacts';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import Title from './Title';
import Spacer from '../Spacer';
import ConfirmationDialog from '../ConfirmationDialog';
import UserChips from './UserChips';
import DepartureProgress from './DepartureProgress';
import baseStyles from '../styles';

class InternalEventDialogView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      tab: 0,
      isDeleteDlgOpen: false,
      isReportDlgOpen: false,
    };
    this.formikRef = React.createRef();
    this.dialogTitleRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    const { props } = this;

    const formikBag = this.formikRef.current;
    if (
      !formikBag ||
      !props.event ||
      !prevProps.event ||
      props.event === prevProps.event
    ) {
      // Exit early if event hasn't been changed
      return;
    }

    formikBag.resetForm({ values: props.event });
    if (!formikBag.isSubmitting) {
      this.showNotification('Event has been updated externally.');

      // Gracefully apply new changes to the form to prevent overwriting user's edits
      const mergedValues = utils.mergeFormikValues(
        prevProps.event,
        formikBag?.dirty ? formikBag.values : null,
        props.event
      );
      formikBag.setValues(mergedValues);
    }
  }

  deleteEvent = () => {
    const { onDelete, event } = this.props;
    onDelete?.(event._id);
  };

  downloadReport = event => {
    if (eventHelper.isUralAirlines(event)) {
      return uralReport.createUralReportExcel(event);
    }
    if (eventHelper.isPobedaAirlines(event)) {
      return dpReport.createDpReportExcel(
        event,
        this.props.events,
        this.props.rampServices
      );
    }
  };

  submitReport = async id => {
    const { onEventReportSubmit } = this.props;
    await onEventReportSubmit?.(id);
  };

  showNotification = (message, severity) => {
    const { onNotificationShow } = this.props;
    const top = mobileHelper.isPortrait()
      ? this.dialogTitleRef.current?.getBoundingClientRect()?.bottom
      : undefined;
    onNotificationShow({ message, severity, top });
  };

  handleTabChange = (e, tab) => {
    this.setState({ tab });
  };

  handleClose = () => {
    const { onClose } = this.props;
    onClose?.();
  };

  handleSubmit = values => {
    const { onSubmit, event } = this.props;
    onSubmit?.(event._id, values);
  };

  handleDelete = () => {
    this.setState({ isDeleteDlgOpen: true });
  };

  handleDeleteDlgClose = () => {
    this.setState({ isDeleteDlgOpen: false });
  };

  handleDeleteDlgConfirm = () => {
    this.deleteEvent();
    this.setState({ isDeleteDlgOpen: false });
  };

  handleReportDlgClose = () => {
    this.setState({ isReportDlgOpen: false });
  };

  handleReportDlgConfirm = async () => {
    try {
      const { event } = this.props;
      await this.submitReport(event._id);
    } catch (err) {
      alert('Could not submit report due to the error.');
    }
    this.setState({ isReportDlgOpen: false });
  };

  handleReportClick = async () => {
    try {
      const { event } = this.props;
      if (eventHelper.isReportSubmitted(event)) {
        this.setState({ isReportDlgOpen: true });
      } else {
        await this.submitReport(event._id);
      }
    } catch (err) {
      alert('Could not submit report due to the error.');
    }
  };

  handleReportDownload = async () => {
    try {
      const { event } = this.props;
      await this.downloadReport(event);
    } catch (err) {
      alert('Could not generate report due to the error.');
    }
  };

  renderTitle = (values, tab, airports) => {
    switch (tab) {
      case 0:
        return (
          <Title
            airports={airports}
            airline={values.airline}
            flightNumber={values.inboundNumber}
            scheduledTime={values.STA}
            estimatedTime={
              values.inbound.ETA ? moment(values.inbound.ETA).format() : null
            }
            actualTime={
              values.inbound.ATA ? moment(values.inbound.ATA).format() : null
            }
            inboundAirport={values.airport}
            outboundAirport={values.inboundAirport}
            tab={tab}
          />
        );
      case 1:
        return (
          <Title
            airports={airports}
            scheduledTime={values.STD}
            estimatedTime={
              values.outbound.ETD ? moment(values.outbound.ETD).format() : null
            }
            actualTime={
              values.outbound.ATD ? moment(values.outbound.ATD).format() : null
            }
            inboundAirport={values.outboundAirport}
            outboundAirport={values.airport}
            tab={tab}
          />
        );
      default:
        return null;
    }
  };

  render() {
    const { isOpen, event, classes, airports, rampServices } = this.props;
    const { tab, isDeleteDlgOpen, isReportDlgOpen } = this.state;

    if (!event) return null;

    const isFlightArrived = event.inbound.ATA;

    const isDpReportButton = tab === 1 && eventHelper.isPobedaAirlines(event);

    const isUralReportButton = tab === 1 && eventHelper.isUralAirlines(event);

    const isReportSubmitted = eventHelper.isReportSubmitted(event);

    return (
      <>
        <Formik
          innerRef={this.formikRef}
          initialValues={event}
          onSubmit={this.handleSubmit}
        >
          {({ submitForm, dirty, values, setFieldValue, isSubmitting }) => {
            const isSaveDisabled = isSubmitting || !dirty;
            const isReportsDisabled = isSubmitting || dirty;
            return (
              <Dialog
                open={isOpen}
                onClose={this.handleClose}
                fullScreen={mobileHelper.isPortrait()}
              >
                <DialogTitle
                  ref={this.dialogTitleRef}
                  className={classes.noPadding}
                >
                  <Tabs
                    value={tab}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    onChange={this.handleTabChange}
                  >
                    <Tab
                      className={classes.tab}
                      classes={{
                        labelIcon: classes.tabLabelIcon,
                      }}
                      icon={<InboundIcon />}
                      label={`${event.airline}-${event.inboundNumber}`}
                    />
                    <Tab
                      className={classes.tab}
                      classes={{
                        labelIcon: classes.tabLabelIcon,
                      }}
                      icon={<OutboundIcon />}
                      label={`${event.airline}-${event.outboundNumber}`}
                    />
                    <Tab
                      className={classes.tab}
                      classes={{
                        labelIcon: classes.tabLabelIcon,
                      }}
                      icon={<PhoneIcon />}
                    />
                  </Tabs>
                  <div className={classes.title}>
                    {this.renderTitle(values, tab, airports)}
                  </div>
                  {(tab === 0 &&
                    moment(values.inbound.STA)
                      .subtract(3, 'hours')
                      .isBefore(moment())) ||
                  (tab === 1 &&
                    moment(values.outbound.STD)
                      .subtract(3, 'hours')
                      .isBefore(moment())) ? (
                    <DepartureProgress
                      tab={tab}
                      STA={values.STA}
                      STD={values.STD}
                      ETA={values.inbound.ETA}
                      ATA={values.inbound.ATA}
                      ETD={values.outbound.ETD}
                      ATD={values.outbound.ATD}
                    />
                  ) : null}
                </DialogTitle>
                <DialogContent
                  className={classNames(classes.noPadding, classes.noOverflowX)}
                >
                  {!isFlightArrived && (
                    <Alert
                      className={classes.flightNotArrivedAlert}
                      severity="warning"
                    >
                      Flight hasn't arrived yet. Set "ATA" field to hide this
                      alert.
                    </Alert>
                  )}
                  <Form className={classes.form}>
                    <Accordion square>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        Allocated Staff
                        <UserChips event={event} />
                      </AccordionSummary>
                      <AccordionDetails>
                        <UserChips
                          className={classes.userChips}
                          event={event}
                        />
                      </AccordionDetails>
                    </Accordion>
                    {tab === 0 && (
                      <InboundForm
                        initialValues={event}
                        setFieldValue={setFieldValue}
                        values={values}
                        rampServices={rampServices}
                      />
                    )}
                    {tab === 1 && (
                      <OutboundForm
                        initialValues={event}
                        setFieldValue={setFieldValue}
                        values={values}
                        rampServices={rampServices}
                      />
                    )}
                    {tab === 2 && (
                      <EventContacts
                        setFieldValue={setFieldValue}
                        values={values}
                      />
                    )}
                  </Form>
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                  <PopupState variant="popover">
                    {popupState => (
                      <>
                        <IconButton size="small" {...bindTrigger(popupState)}>
                          <MoreVertIcon />
                        </IconButton>
                        <Menu {...bindMenu(popupState)}>
                          <MenuItem onClick={this.handleDelete}>
                            <Typography variant="button" color="secondary">
                              DELETE EVENT
                            </Typography>
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </PopupState>

                  {isUralReportButton && (
                    <ButtonGroup
                      disabled={isReportsDisabled}
                      variant="outlined"
                      size="small"
                    >
                      <Button
                        className={classNames({
                          [classes.root]: isReportSubmitted,
                        })}
                        onClick={this.handleReportClick}
                      >
                        {isReportSubmitted ? 'Reported' : 'Report'}
                      </Button>
                      <Tooltip title="Download report" disableFocusListener>
                        <Button onClick={this.handleReportDownload}>
                          <GetAppIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </ButtonGroup>
                  )}
                  {isDpReportButton && (
                    <Tooltip title="Download report" disableFocusListener>
                      <Button
                        disabled={isReportsDisabled}
                        onClick={this.handleReportDownload}
                        startIcon={<GetAppIcon fontSize="small" />}
                      >
                        Report
                      </Button>
                    </Tooltip>
                  )}
                  <Spacer />
                  <Button
                    disabled={isSaveDisabled}
                    onClick={submitForm}
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button onClick={this.handleClose} color="primary">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            );
          }}
        </Formik>
        <ConfirmationDialog
          open={isDeleteDlgOpen}
          title={'Delete Event'}
          text={'Are you sure to delete the event?'}
          onClose={this.handleDeleteDlgClose}
          onConfirm={this.handleDeleteDlgConfirm}
        />
        <ConfirmationDialog
          open={isReportDlgOpen}
          title={'Send Report'}
          text={'Are you sure to re-submit the report?'}
          onClose={this.handleReportDlgClose}
          onConfirm={this.handleReportDlgConfirm}
        />
      </>
    );
  }
}

const styles = theme => ({
  ...baseStyles(theme),
  tab: {
    minHeight: 'initial',
    maxHeight: 52,
    padding: 0,
  },
  tabLabelIcon: {
    paddingTop: theme.spacing(0.5),
    '& .MuiTab-wrapper > *:first-child': {
      marginBottom: 0,
    },
  },
  tabWrapper: {},
  userChips: {
    marginTop: theme.spacing(1),
  },
  root: {
    color: theme.palette.success.main,
  },
  form: {
    padding: 0,
  },
  dialogActions: {
    zIndex: 1,
    boxShadow:
      '0px -3px 1px -2px rgba(0,0,0,0.2),0px -2px 2px 0px rgba(0,0,0,0.14),0px -1px 5px 0px rgba(0,0,0,0.12)',
    padding: `${theme.spacing(0.5)} ${theme.spacing()}`,
  },
  flightNotArrivedAlert: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
});

export const EventDialogView = withStyles(styles)(InternalEventDialogView);
